import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { jitteredPollMs } from '~/utils/jitter';

import { RUNS_LIST_MOVING_POLL_MS, refetchOnShow } from '../constants/polling';
import { getRunStatusCounts, listRuns } from '../services/runs.service';
import type { CatalogPage } from '../types/catalog-enrichment.types';
import type { RunRead } from '../types/run.api.types';
import type { RunsFilterParams, RunsListParams } from '../types/runs.types';

/** List statuses that advance without anyone acting — an open gate waits for a person. */
const MOVING_STATUSES = new Set(['PENDING', 'RUNNING', 'DELIVERING']);

/** True while some row on the page advances without anyone acting. */
function hasMovingRow(page: CatalogPage<RunRead> | undefined): boolean {
  return !!page?.items.some((run) => MOVING_STATUSES.has(run.listStatus));
}

/**
 * One page of the run table; keeps the previous page on screen while the next loads. Refreshes
 * every ~15 s (±15%) while a row on the page is moving, so its progress bar advances live, and
 * stops once nothing on the page is; never in a hidden tab (a missed poll runs once on show).
 */
export function useRunsList(params: RunsListParams) {
  return useQuery({
    queryKey: queryKeys.catalogService.runs.list({ ...params }),
    queryFn: () => listRuns(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      hasMovingRow(query.state.data) ? jitteredPollMs(query, RUNS_LIST_MOVING_POLL_MS) : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (query) =>
      refetchOnShow(
        query.state.dataUpdatedAt,
        hasMovingRow(query.state.data) ? RUNS_LIST_MOVING_POLL_MS : false,
      ),
  });
}

/** Badge counts for the run-list tabs under the current filters (the tab itself excluded). */
export function useRunsStatusCounts(params: RunsFilterParams) {
  return useQuery({
    queryKey: queryKeys.catalogService.runs.statusCounts({ ...params }),
    queryFn: () => getRunStatusCounts(params),
    placeholderData: keepPreviousData,
  });
}
